import { z } from "zod";
import { sourceChannels } from "./attribution";
import { emailSchema } from "./auth";
import { leadFieldsSchema, phoneField } from "./lead";

/**
 * Stage colours are stored as lower-case `#rrggbb`. The presets are the
 * original palette's light-theme values (tokens.css); the web keeps their
 * hand-tuned dark-theme variants and derives the rest.
 */
export const stageColorPresets = {
  lavender: "#8a6ad6",
  rose: "#d0496a",
  amber: "#c27a12",
  sky: "#2f86c9",
  mint: "#2f9a6f",
  peach: "#d9683b",
  teal: "#1f8f92",
  slate: "#66708a",
} as const;
export type StageColorPreset = keyof typeof stageColorPresets;
export const stageColorPresetNames = Object.keys(stageColorPresets) as StageColorPreset[];
export type StageColor = string;
export const DEFAULT_STAGE_COLOR: StageColor = stageColorPresets.lavender;

export const stageColorSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^#[0-9a-f]{6}$/, "Choose a colour as a hex code, like #8a6ad6.");

export const STAGE_DELETE_CONFIRMATION = "DELETE ALL LEADS";

const isoDate = z.iso.date("Use a valid date.");

export const leadSorts = ["priority", "newest", "oldest", "name"] as const;
export type LeadSort = (typeof leadSorts)[number];

export const followUpFilters = ["overdue", "today", "due", "upcoming", "none"] as const;
export type FollowUpFilter = (typeof followUpFilters)[number];

/** All Leads filters (also used as URL search params in the web app). */
export const leadListQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  stageId: z.uuid().optional(),
  serviceId: z.uuid().optional(),
  channel: z.enum(sourceChannels).optional(),
  campaign: z.string().trim().max(100).optional(),
  followUp: z.enum(followUpFilters).optional(),
  createdFrom: isoDate.optional(),
  createdTo: isoDate.optional(),
  /** Created by me or assigned to me ("me" always comes from the session). */
  mine: z.stringbool().optional(),
  /** Leads I haven't reviewed yet. */
  unreviewed: z.stringbool().optional(),
  /** A team member's id, or "none" for unassigned leads. */
  assignee: z.union([z.literal("none"), z.string().trim().min(1).max(100)]).optional(),
  campaignId: z.uuid().optional(),
  keyword: z.string().trim().toLowerCase().min(1).max(100).optional(),
  sort: z.enum(leadSorts).default("priority"),
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type LeadListQuery = z.input<typeof leadListQuerySchema>;

/** Pipeline board filters: a subset of the list filters (stage is the column). */
export const pipelineQuerySchema = leadListQuerySchema.pick({
  q: true,
  serviceId: true,
  channel: true,
  campaign: true,
  followUp: true,
  createdFrom: true,
  createdTo: true,
  mine: true,
  unreviewed: true,
  assignee: true,
  campaignId: true,
  keyword: true,
});
export type PipelineQuery = z.input<typeof pipelineQuerySchema>;

/** Leads shown per column before "Load more". */
export const PIPELINE_PAGE_SIZE = 50;

/** Manual "Add lead" uses the same fields as the public form, plus who the lead goes to. */
export const createLeadSchema = leadFieldsSchema.extend({
  /** "rotation": the next rep in the round robin; "none": unassigned; otherwise a team member's id. */
  assignTo: z
    .union([z.literal("rotation"), z.literal("none"), z.string().trim().min(1)])
    .default("rotation"),
});
export type CreateLeadInput = z.input<typeof createLeadSchema>;
export type CreateLead = z.output<typeof createLeadSchema>;

/** Editable lead details. Everything optional; nulls clear optional fields. */
export const updateLeadSchema = z
  .object({
    firstName: z.string().trim().min(1, "Enter a first name.").max(80),
    lastName: z.string().trim().min(1, "Enter a last name.").max(80),
    email: emailSchema,
    phone: phoneField,
    company: z.string().trim().max(120).nullable(),
    serviceId: z.uuid().nullable(),
    message: z.string().trim().max(2000).nullable(),
    marketingConsent: z.boolean(),
    stageId: z.uuid(),
    /** Follow-up day (Australia/Sydney), or null to clear. */
    nextFollowUpOn: isoDate.nullable(),
  })
  .partial()
  .strict();
export type UpdateLeadInput = z.input<typeof updateLeadSchema>;

/** Reposition a lead: between two neighbours, optionally into another stage. */
export const moveLeadSchema = z
  .object({
    stageId: z.uuid().optional(),
    beforeId: z.uuid().nullable().optional(),
    afterId: z.uuid().nullable().optional(),
    /** Jump to the very top or bottom of the whole priority list. */
    to: z.enum(["top", "bottom"]).optional(),
  })
  .strict();
export type MoveLeadInput = z.input<typeof moveLeadSchema>;

export const noteSchema = z.object({
  body: z
    .string()
    .trim()
    .min(1, "Write a note first.")
    .max(5000, "Keep notes under 5,000 characters."),
});

export const stageNameSchema = z
  .string()
  .trim()
  .min(1, "Enter a stage name.")
  .max(40, "Keep stage names under 40 characters.");

export const createStageSchema = z.object({
  name: stageNameSchema,
  color: stageColorSchema.default(DEFAULT_STAGE_COLOR),
});

export const updateStageSchema = z
  .object({
    name: stageNameSchema,
    color: stageColorSchema,
    isDefault: z.literal(true),
  })
  .partial()
  .strict();

export const moveStageSchema = z.object({
  beforeId: z.uuid().nullable().optional(),
  afterId: z.uuid().nullable().optional(),
});

/** Replace a lead's assignees (empty array unassigns everyone). */
export const assignLeadSchema = z.object({
  userIds: z.array(z.string().trim().min(1).max(100)).max(50),
});
export type AssignLeadInput = z.input<typeof assignLeadSchema>;

export const deleteStageSchema = z.object({
  confirmation: z.literal(STAGE_DELETE_CONFIRMATION, {
    error: `Type ${STAGE_DELETE_CONFIRMATION} to confirm.`,
  }),
  newDefaultStageId: z.uuid().optional(),
});
